import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function HeroPlanet() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let renderer, scene, cam, planetGroup, wire, ringGroup;
    const meshes = [];
    let disposed = false;

    function init(w, h) {
      const probe = document.createElement('div');
      probe.style.color = 'var(--accent)';
      probe.style.display = 'none';
      document.body.appendChild(probe);
      const raw = getComputedStyle(probe).color;
      document.body.removeChild(probe);
      const cv = document.createElement('canvas').getContext('2d');
      cv.fillStyle = raw;
      const accent = new THREE.Color(cv.fillStyle);

      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      el.appendChild(renderer.domElement);

      scene = new THREE.Scene();
      cam = new THREE.PerspectiveCamera(60, w / h, 0.1, 100);
      cam.position.set(0, 0.4, 5.5);
      cam.lookAt(0, 0, 0);

      planetGroup = new THREE.Group();
      planetGroup.rotation.z = -0.22;
      scene.add(planetGroup);

      const bodyMat = new THREE.MeshBasicMaterial({ color: 0x080808 });
      const body = new THREE.Mesh(new THREE.SphereGeometry(1.5, 64, 64), bodyMat);
      planetGroup.add(body);
      meshes.push(body);

      const wireGeo = new THREE.IcosahedronGeometry(1.505, 5);
      const wireMat = new THREE.MeshBasicMaterial({ color: accent, wireframe: true,
                                                    transparent: true, opacity: 0.42 });
      wire = new THREE.Mesh(wireGeo, wireMat);
      planetGroup.add(wire);
      meshes.push(wire);

      const haloGeo = new THREE.SphereGeometry(1.62, 64, 64);
      const haloMat = new THREE.MeshBasicMaterial({ color: accent, transparent: true,
                                                    opacity: 0.10, side: THREE.BackSide });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      planetGroup.add(halo);
      meshes.push(halo);

      const limbGeo = new THREE.TorusGeometry(1.502, 0.008, 8, 200);
      const limbMat = new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.85 });
      const limb = new THREE.Mesh(limbGeo, limbMat);
      limb.rotation.x = Math.PI / 2;
      planetGroup.add(limb);
      meshes.push(limb);

      ringGroup = new THREE.Group();
      ringGroup.rotation.x = Math.PI / 2.6;
      ringGroup.rotation.z = 0.15;
      scene.add(ringGroup);

      const ring1 = new THREE.Mesh(new THREE.TorusGeometry(2.55, 0.006, 8, 256),
                                    new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.8 }));
      const ring2 = new THREE.Mesh(new THREE.TorusGeometry(2.65, 0.003, 6, 256),
                                    new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.35 }));
      const ring3 = new THREE.Mesh(new THREE.TorusGeometry(2.72, 0.002, 6, 256),
                                    new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.18 }));
      ringGroup.add(ring1, ring2, ring3);
      meshes.push(ring1, ring2, ring3);

      const moonMat = new THREE.MeshBasicMaterial({ color: accent });
      const moon = new THREE.Mesh(new THREE.SphereGeometry(0.04, 16, 16), moonMat);
      ringGroup.add(moon);
      meshes.push(moon);

      const t0 = performance.now();
      const tick = () => {
        if (disposed) return;
        const dt = (performance.now() - t0) / 1000;
        planetGroup.rotation.y = dt * 0.12;
        wire.rotation.y = dt * 0.15;
        ringGroup.rotation.y = dt * 0.06;
        moon.position.set(Math.cos(dt * 0.35) * 2.55, 0, Math.sin(dt * 0.35) * 2.55);
        renderer.render(scene, cam);
        raf = requestAnimationFrame(tick);
      };
      tick();
    }

    let initRaf = 0;

    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (!width || !height) return;
      if (!renderer) {
        cancelAnimationFrame(initRaf);
        initRaf = requestAnimationFrame(() => {
          initRaf = requestAnimationFrame(() => {
            if (disposed || renderer) return;
            const w = el.clientWidth, h = el.clientHeight;
            if (w && h) init(w, h);
          });
        });
      } else {
        cam.aspect = width / height;
        cam.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
    });
    ro.observe(el);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(initRaf);
      ro.disconnect();
      if (renderer) {
        renderer.dispose();
        meshes.forEach(m => { m.geometry.dispose(); m.material.dispose(); });
        if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div className="hero-planet" ref={ref} aria-hidden="true" />;
}
